package features.booking

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.DirectionsBus
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp

/**
 * Layout visual del vehículo (mismo look-and-feel que el cliente en BookingScreen).
 * Reusado en Reasignar cancelación y en Reserva en Efectivo.
 * - Si [vehiculo] es null, se usa un layout por defecto de 13 asientos.
 * - Los asientos ocupados salen en gris no-clickeables; los seleccionados en cian;
 *   los libres en verde.
 */
@Composable
fun MapaAsientosVehiculo(
    vehiculo: VehiculoConfig?,
    ocupados: Set<Int>,
    seleccionados: Set<Int>,
    onToggleAsiento: (Int) -> Unit,
    modifier: Modifier = Modifier
) {
    val layoutDefault = VehiculoConfig(
        nombre = "Default", capacidad = 13, columnas = 4,
        filas = listOf(
            FilaVehiculo(listOf(-1, 0, 0, 1)),
            FilaVehiculo(listOf(2, 3, 0, 4)),
            FilaVehiculo(listOf(5, 6, 0, 7)),
            FilaVehiculo(listOf(8, 9, 0, 10)),
            FilaVehiculo(listOf(11, 12, 13, 0)),
        )
    )
    // Elegir la fuente del layout:
    //  1) VehiculoConfig con filas → úsalo tal cual (más fiel: layout real del vehículo).
    //  2) VehiculoConfig con capacidad pero SIN filas → generar layout genérico auto (fila
    //     del volante + filas 2-2 con pasillo, hasta cubrir la capacidad).
    //  3) null → layout default (13).
    val v = when {
        vehiculo != null && vehiculo.filas.isNotEmpty() -> vehiculo
        vehiculo != null && vehiculo.capacidad > 0 -> generarLayoutGenerico(vehiculo.capacidad)
        else -> layoutDefault
    }

    Column(
        modifier = modifier.fillMaxWidth(),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
      CarroceriaVehiculo(filas = v.filas) {
        v.filas.forEach { fila ->
            Row(
                modifier = Modifier.width(280.dp),
                horizontalArrangement = Arrangement.spacedBy(10.dp)
            ) {
                fila.asientos.forEach { celda ->
                    Box(modifier = Modifier.weight(1f), contentAlignment = Alignment.Center) {
                        when {
                            celda == -1 -> {
                                Box(
                                    modifier = Modifier.size(58.dp)
                                        .background(Color(0xFF455A64), RoundedCornerShape(topStart = 14.dp, topEnd = 14.dp, bottomStart = 8.dp, bottomEnd = 8.dp)),
                                    contentAlignment = Alignment.Center
                                ) {
                                    Column(horizontalAlignment = Alignment.CenterHorizontally) {
                                        Icon(Icons.Filled.DirectionsBus, contentDescription = null, tint = Color.White, modifier = Modifier.size(18.dp))
                                        Text("VOL", color = Color.White, fontWeight = FontWeight.Bold, style = MaterialTheme.typography.labelSmall)
                                    }
                                }
                            }
                            celda == 0 -> Spacer(modifier = Modifier.size(58.dp))
                            else -> {
                                val estado = when {
                                    celda in ocupados -> SeatStatus.OCCUPIED
                                    celda in seleccionados -> SeatStatus.SELECTED
                                    else -> SeatStatus.AVAILABLE
                                }
                                SeatItem(Seat(id = celda, status = estado)) { onToggleAsiento(celda) }
                            }
                        }
                    }
                }
            }
            Spacer(modifier = Modifier.height(10.dp))
        }
      }
        Spacer(modifier = Modifier.height(10.dp))

        // Leyenda
        Row(
            modifier = Modifier.padding(top = 6.dp),
            horizontalArrangement = Arrangement.spacedBy(12.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            LeyendaAsiento(SmartVanColors.Verde, "Libre")
            LeyendaAsiento(SmartVanColors.CyanAccent, "Elegido")
            LeyendaAsiento(Color(0xFFD0D0D0), "Ocupado")
        }
    }
}

/** Estado de cada asiento en el manifiesto del chofer. */
enum class EstadoAsientoChofer {
    LIBRE,           // gris — nadie compró
    COMPRADO,        // rojo — pagado, no ha abordado
    ABORDADO,        // verde — QR escaneado / asistencia marcada
    PENDIENTE_PAGO   // amarillo/naranja — efectivo pendiente de confirmar
}

/**
 * Mapa visual del vehículo para el CHOFER durante el viaje: mismos assets del
 * cliente pero con estados y colores diferentes (comprado / abordado / pendiente
 * de pago / vacío). No es clickeable (solo lectura); el chofer verifica con QR.
 */
@Composable
fun MapaAsientosChofer(
    vehiculo: VehiculoConfig?,
    estadoAsiento: Map<Int, EstadoAsientoChofer>,
    modifier: Modifier = Modifier,
    seleccionado: Int? = null,
    onAsientoClick: ((Int) -> Unit)? = null
) {
    val layoutDefault = VehiculoConfig(
        nombre = "Default", capacidad = 13, columnas = 4,
        filas = listOf(
            FilaVehiculo(listOf(-1, 0, 0, 1)),
            FilaVehiculo(listOf(2, 3, 0, 4)),
            FilaVehiculo(listOf(5, 6, 0, 7)),
            FilaVehiculo(listOf(8, 9, 0, 10)),
            FilaVehiculo(listOf(11, 12, 13, 0)),
        )
    )
    val v = when {
        vehiculo != null && vehiculo.filas.isNotEmpty() -> vehiculo
        vehiculo != null && vehiculo.capacidad > 0 -> generarLayoutGenerico(vehiculo.capacidad)
        else -> layoutDefault
    }

    // Paleta: gris=libre, rojo=comprado sin abordar, verde=abordado, ámbar=pendiente pago.
    fun colorAsiento(e: EstadoAsientoChofer): Color = when (e) {
        EstadoAsientoChofer.LIBRE -> Color(0xFFD0D0D0)
        EstadoAsientoChofer.COMPRADO -> Color(0xFFEF5350)
        EstadoAsientoChofer.ABORDADO -> SmartVanColors.Verde
        EstadoAsientoChofer.PENDIENTE_PAGO -> Color(0xFFFFA726)
    }
    fun borde(e: EstadoAsientoChofer): Color = when (e) {
        EstadoAsientoChofer.LIBRE -> Color(0xFFBDBDBD)
        EstadoAsientoChofer.COMPRADO -> Color(0xFFC62828)
        EstadoAsientoChofer.ABORDADO -> Color(0xFF388E3C)
        EstadoAsientoChofer.PENDIENTE_PAGO -> Color(0xFFF57C00)
    }

    Column(
        modifier = modifier.fillMaxWidth(),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
      CarroceriaVehiculo(filas = v.filas) {

        v.filas.forEach { fila ->
            Row(
                modifier = Modifier.width(280.dp),
                horizontalArrangement = Arrangement.spacedBy(10.dp)
            ) {
                fila.asientos.forEach { celda ->
                    Box(modifier = Modifier.weight(1f), contentAlignment = Alignment.Center) {
                        when {
                            celda == -1 -> {
                                Box(
                                    modifier = Modifier.size(58.dp)
                                        .background(Color(0xFF455A64), RoundedCornerShape(topStart = 14.dp, topEnd = 14.dp, bottomStart = 8.dp, bottomEnd = 8.dp)),
                                    contentAlignment = Alignment.Center
                                ) {
                                    Column(horizontalAlignment = Alignment.CenterHorizontally) {
                                        Icon(Icons.Filled.DirectionsBus, contentDescription = null, tint = Color.White, modifier = Modifier.size(18.dp))
                                        Text("VOL", color = Color.White, fontWeight = FontWeight.Bold, style = MaterialTheme.typography.labelSmall)
                                    }
                                }
                            }
                            celda == 0 -> Spacer(modifier = Modifier.size(58.dp))
                            else -> {
                                val estado = estadoAsiento[celda] ?: EstadoAsientoChofer.LIBRE
                                val bg = colorAsiento(estado)
                                val bordeCol = borde(estado)
                                val esSel = seleccionado == celda
                                val clickable = onAsientoClick != null && estado != EstadoAsientoChofer.LIBRE
                                Column(
                                    horizontalAlignment = Alignment.CenterHorizontally,
                                    modifier = if (clickable)
                                        Modifier.clickable { onAsientoClick!!(celda) }
                                    else Modifier
                                ) {
                                    Box(modifier = Modifier.width(40.dp).height(8.dp)
                                        .background(bg, RoundedCornerShape(topStart = 8.dp, topEnd = 8.dp)))
                                    Box(
                                        modifier = Modifier.width(44.dp).height(44.dp)
                                            .background(bg, RoundedCornerShape(10.dp))
                                            .then(
                                                if (esSel) Modifier.border(
                                                    androidx.compose.foundation.BorderStroke(3.dp, SmartVanColors.AzulOscuro),
                                                    RoundedCornerShape(10.dp)
                                                ) else Modifier
                                            ),
                                        contentAlignment = Alignment.Center
                                    ) {
                                        Text(
                                            "$celda",
                                            fontWeight = FontWeight.Black,
                                            color = if (estado == EstadoAsientoChofer.LIBRE) Color(0xFF616161) else Color.White,
                                            fontSize = 14.sp
                                        )
                                    }
                                }
                            }
                        }
                    }
                }
            }
            Spacer(modifier = Modifier.height(10.dp))
        }
      }
        Spacer(modifier = Modifier.height(10.dp))

        // Leyenda de 4 estados
        Row(
            modifier = Modifier.padding(top = 6.dp),
            horizontalArrangement = Arrangement.spacedBy(10.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            LeyendaAsiento(colorAsiento(EstadoAsientoChofer.LIBRE), "Vacío")
            LeyendaAsiento(colorAsiento(EstadoAsientoChofer.COMPRADO), "Comprado")
            LeyendaAsiento(colorAsiento(EstadoAsientoChofer.ABORDADO), "Abordó")
            LeyendaAsiento(colorAsiento(EstadoAsientoChofer.PENDIENTE_PAGO), "Debe pago")
        }
    }
}

/**
 * Genera un layout genérico tipo van/sprinter cuando el viaje no tiene un layout de vehículo
 * asignado pero sí una capacidad conocida. Layout: fila 1 = volante + asiento 1; filas
 * siguientes = par izquierdo, pasillo, par derecho (2-2). Numera los asientos correlativos.
 */
// internal: el repositorio lo usa para que el fallback por capacidad ya traiga filas.
// Sin filas, las pantallas que iteran filas.forEach directo renderizan CERO asientos.
internal fun generarLayoutGenerico(capacidad: Int): VehiculoConfig {
    val filas = mutableListOf<FilaVehiculo>()
    // Fila del chofer: [volante, vacío, vacío, asiento 1] — copiloto va junto al chofer.
    filas.add(FilaVehiculo(listOf(-1, 0, 0, 1)))
    var siguiente = 2
    while (siguiente <= capacidad) {
        val izq1 = if (siguiente <= capacidad) siguiente++ else 0
        val izq2 = if (siguiente <= capacidad) siguiente++ else 0
        val der1 = if (siguiente <= capacidad) siguiente++ else 0
        val der2 = if (siguiente <= capacidad) siguiente++ else 0
        // Formato: [izq_ventana, izq_pasillo, der_pasillo, der_ventana]
        filas.add(FilaVehiculo(listOf(izq1, izq2, der1, der2)))
    }
    return VehiculoConfig(nombre = "Genérico", capacidad = capacidad, columnas = 4, filas = filas)
}

@Composable
private fun LeyendaAsiento(color: Color, texto: String) {
    Row(verticalAlignment = Alignment.CenterVertically) {
        Box(modifier = Modifier.size(12.dp).background(color, RoundedCornerShape(3.dp)))
        Spacer(modifier = Modifier.width(4.dp))
        Text(texto, style = MaterialTheme.typography.labelSmall, color = Color.DarkGray)
    }
}

/**
 * Carrocería del vehículo alrededor del mapa de asientos.
 *
 * Es sólo presentación, pero ayuda a que el pasajero ubique dónde va a sentarse: el frente
 * (parabrisas y cofre) se dibuja del lado donde está el volante en el layout, no siempre
 * arriba. Así el esquema coincide con cómo el admin capturó la unidad.
 */
@Composable
fun CarroceriaVehiculo(
    filas: List<FilaVehiculo>,
    modifier: Modifier = Modifier,
    contenido: @Composable ColumnScope.() -> Unit
) {
    val idxVolante = filas.indexOfFirst { f -> f.asientos.any { it == -1 } }
    // Sin volante declarado se asume frente arriba, que es como se venían dibujando.
    val frenteAbajo = idxVolante >= 0 && idxVolante >= filas.size - 1 && filas.size > 1

    val chasis = Color(0xFFB9C2CC)
    val interior = Color(0xFFF2F5F8)
    val cristal = Color(0xFFBFD8E6)

    @Composable
    fun Frente() {
        Column(
            modifier = Modifier.fillMaxWidth(),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            // Parabrisas
            Box(
                modifier = Modifier.fillMaxWidth(0.72f).height(14.dp)
                    .background(cristal, RoundedCornerShape(10.dp))
            )
            Spacer(modifier = Modifier.height(4.dp))
            Text("FRENTE", color = Color(0xFF90A4AE), fontSize = 8.sp, fontWeight = FontWeight.Bold)
        }
    }

    Box(modifier = modifier) {
        Column(
            modifier = Modifier
                .background(
                    chasis,
                    if (frenteAbajo) RoundedCornerShape(topStart = 14.dp, topEnd = 14.dp, bottomStart = 44.dp, bottomEnd = 44.dp)
                    else RoundedCornerShape(topStart = 44.dp, topEnd = 44.dp, bottomStart = 14.dp, bottomEnd = 14.dp)
                )
                .padding(6.dp),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Column(
                modifier = Modifier
                    .background(
                        interior,
                        if (frenteAbajo) RoundedCornerShape(topStart = 10.dp, topEnd = 10.dp, bottomStart = 38.dp, bottomEnd = 38.dp)
                        else RoundedCornerShape(topStart = 38.dp, topEnd = 38.dp, bottomStart = 10.dp, bottomEnd = 10.dp)
                    )
                    .padding(horizontal = 10.dp, vertical = 12.dp),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                if (!frenteAbajo) { Frente(); Spacer(modifier = Modifier.height(10.dp)) }
                contenido()
                if (frenteAbajo) { Spacer(modifier = Modifier.height(10.dp)); Frente() }
            }
        }
    }
}
